/**
 * Row Component Types
 *
 * Shared type definitions for all entity row components.
 * These types ensure consistency across different row implementations.
 */

import type { ReactNode } from 'react';

/**
 * Action types available for fast actions
 *
 * Standard actions available across all entity rows and chat messages:
 * - task: Create a task
 * - event: Create a calendar event
 * - email: Send an email
 * - call: Make a phone call
 * - message: Send a message (SMS, WhatsApp, Telegram)
 * - reminder: Create a reminder
 * - note: Create a note
 *
 * Additional actions for specific contexts:
 * - videocall: Start a video call
 * - file: Attach a file
 * - invoice: Create an invoice
 */
export type ActionType =
  | 'task'
  | 'event'
  | 'email'
  | 'call'
  | 'message'
  | 'reminder'
  | 'note'
  | 'videocall'
  | 'file'
  | 'invoice';

/**
 * Standard fast actions for rows and messages
 * This is the default set used across all entity rows and chat messages
 */
export const STANDARD_FAST_ACTIONS: ActionType[] = [
  'task',
  'event',
  'email',
  'call',
  'message',
  'reminder',
  'note',
];

/**
 * Base props for all row components
 */
export interface BaseRowProps<T = any> {
  /** The entity to display */
  entity: T;

  /** Entity type derived from ID prefix (e.g., 'task', 'contact') */
  entityType: string;

  // Customization slots
  /** Left slot - primary action (check, eye, archive, star) */
  leftSlot?: ReactNode;

  /** Custom icon or avatar content for left zone */
  icon?: ReactNode;

  /** User IDs for avatar stack */
  avatars?: string[];

  /** Meta information displayed below title (dates, tags, etc.) */
  metaContent?: ReactNode;

  /** Additional content for right zone (e.g., timer button) */
  rightContent?: ReactNode;

  // Actions
  /** Available fast actions for this entity */
  availableActions?: ActionType[];

  /** Count of linked entities per action type (for icon coloring) */
  linkedActionCounts?: Record<ActionType, number>;

  /** Handler when title is clicked */
  onTitleClick?: (entity: T) => void;

  /** Handler when a fast action is clicked */
  onActionClick?: (actionType: ActionType, entityId: string) => void;

  // Styling
  /** Visual variant of the row */
  variant?: 'default' | 'compact';

  /** Whether entity is marked as done (applies strikethrough) */
  done?: boolean;

  /** Additional CSS class names */
  className?: string;
}

/**
 * Row meta information props
 * Used for consistent meta data display
 */
export interface RowMetaProps {
  /** Optional date range */
  startDate?: string;
  dueDate?: string;

  /** Tags associated with entity */
  tags?: string[];

  /** Additional meta items */
  items?: Array<{
    icon?: ReactNode;
    label: string;
    value: string;
    color?: string;
  }>;
}
